/**
 * Per-tab "new entry" notifications for demand and debtor records.
 * Compares a content signature against the last-seen value in localStorage.
 * First observation seeds as seen so existing content does not false-notify.
 * Only activity from other people (teammates, attorney partners, system) notifies.
 */

const STORAGE_KEY = 'demandjet.recordTabSeen';

function storage() {
  try {
    return globalThis.localStorage;
  } catch {
    return null;
  }
}

function normalizeIdentity(value) {
  return String(value || '')
    .trim()
    .toLowerCase();
}

export function loadTabSeenMap() {
  const store = storage();
  if (!store) return {};
  try {
    const raw = store.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

export function saveTabSeenMap(map) {
  const store = storage();
  if (!store) return;
  try {
    store.setItem(STORAGE_KEY, JSON.stringify(map || {}));
  } catch {
    // Quota / private mode — ignore.
  }
}

export function tabSeenKey(kind, recordId, tabId) {
  return `${String(kind || '').trim()}:${String(recordId || '').trim()}:${String(tabId || '').trim()}`;
}

/**
 * True when the entry was made by someone other than the current viewer
 * (attorney partner, system, or another org user). Own edits never notify.
 */
export function isForeignEntry(entry = {}, viewer = {}) {
  const viewerEmail = normalizeIdentity(viewer.email);
  const viewerName = normalizeIdentity(viewer.displayName || viewer.name);
  const role = String(entry.Sender_Role || entry.Actor_Role || '')
    .trim()
    .toUpperCase();
  const email = normalizeIdentity(
    entry.Sender_Email || entry.authorEmail || entry.Actor_Email || entry.email
  );
  const name = normalizeIdentity(
    entry.Sender_Name || entry.authorName || entry.Actor_Name || entry.author
  );

  if (['ATTORNEY', 'SYSTEM', 'ADMIN', 'PARTNER'].includes(role)) return true;

  if (viewerEmail && email) return email !== viewerEmail;
  if (name === 'you') return false;
  if (viewerName && name) return name !== viewerName;

  // No reliable actor identity — do not notify (avoids false positives).
  if (!role && !email && !name) return false;
  if (role === 'CLIENT') return Boolean(email || (name && name !== 'you'));
  return false;
}

export function filterForeignEntries(entries = [], viewer = {}) {
  return (Array.isArray(entries) ? entries : []).filter((entry) =>
    isForeignEntry(entry, viewer)
  );
}

/**
 * Build a stable fingerprint for a list of dated entries.
 * Uses count + latest id + latest timestamp so adds and replacements both notify.
 */
export function fingerprintEntries(
  entries = [],
  {
    idKeys = ['ROWID', 'id'],
    atKeys = ['Created_At', 'createdAt', 'Updated_At', 'updatedAt', 'Paid_At'],
  } = {}
) {
  const list = Array.isArray(entries) ? entries.filter(Boolean) : [];
  if (!list.length) return '0';

  let latest = list[0];
  let latestAt = entryTimestamp(latest, atKeys);

  for (let i = 1; i < list.length; i += 1) {
    const row = list[i];
    const at = entryTimestamp(row, atKeys);
    if (at >= latestAt) {
      latest = row;
      latestAt = at;
    }
  }

  const id = entryId(latest, idKeys) || '';
  return `${list.length}:${id}:${latestAt || 0}`;
}

/** Fingerprint only other people's entries for tab notification dots. */
export function fingerprintForeignEntries(entries = [], viewer = {}, options) {
  return fingerprintEntries(filterForeignEntries(entries, viewer), options);
}

function entryId(row, idKeys) {
  for (const key of idKeys) {
    const value = row?.[key];
    if (value != null && String(value).trim()) return String(value).trim();
  }
  return '';
}

function entryTimestamp(row, atKeys) {
  for (const key of atKeys) {
    const value = row?.[key];
    if (!value) continue;
    const ms = Date.parse(String(value));
    if (Number.isFinite(ms)) return ms;
  }
  return 0;
}

export function getSeenSignature(kind, recordId, tabId, map = loadTabSeenMap()) {
  if (!recordId || !tabId) return null;
  const key = tabSeenKey(kind, recordId, tabId);
  return Object.prototype.hasOwnProperty.call(map, key) ? String(map[key] ?? '') : null;
}

export function markTabSeen(kind, recordId, tabId, signature, map = loadTabSeenMap()) {
  if (!recordId || !tabId) return map;
  const key = tabSeenKey(kind, recordId, tabId);
  const next = { ...map, [key]: String(signature ?? '0') };
  saveTabSeenMap(next);
  return next;
}

/**
 * Whether the tab should show a new-entry notification.
 * When no baseline exists yet, seeds last-seen and returns false.
 */
export function hasTabNotification(
  kind,
  recordId,
  tabId,
  currentSignature,
  map = loadTabSeenMap()
) {
  if (!recordId || !tabId) return false;
  const current = String(currentSignature ?? '0');
  const seen = getSeenSignature(kind, recordId, tabId, map);
  if (seen == null) {
    markTabSeen(kind, recordId, tabId, current, map);
    return false;
  }
  if (current === '0') return false;
  return seen !== current;
}

/**
 * Compute unread flags for every tab signature. The active tab is always clear
 * and is marked seen with the latest signature.
 */
export function resolveTabNotifications({
  kind,
  recordId,
  signatures = {},
  activeTab = '',
  map = loadTabSeenMap(),
} = {}) {
  let nextMap = { ...map };
  const unread = {};
  const active = String(activeTab || '');

  for (const [tabId, signature] of Object.entries(signatures || {})) {
    const key = tabSeenKey(kind, recordId, tabId);
    const current = String(signature ?? '0');
    if (!Object.prototype.hasOwnProperty.call(nextMap, key)) {
      nextMap[key] = current;
    } else if (String(nextMap[key] ?? '') === '0' && current !== '0') {
      // First real content after an empty baseline — seed without notifying.
      nextMap[key] = current;
    }
  }

  if (recordId && active && Object.prototype.hasOwnProperty.call(signatures, active)) {
    const key = tabSeenKey(kind, recordId, active);
    nextMap[key] = String(signatures[active] ?? '0');
  }

  saveTabSeenMap(nextMap);

  for (const [tabId, signature] of Object.entries(signatures || {})) {
    if (tabId === active) {
      unread[tabId] = false;
      continue;
    }
    const current = String(signature ?? '0');
    if (current === '0') {
      unread[tabId] = false;
      continue;
    }
    const key = tabSeenKey(kind, recordId, tabId);
    unread[tabId] = String(nextMap[key] ?? '') !== current;
  }

  return { unread, map: nextMap };
}
